/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Appointment registration modal for business events.
 * Lets users register for a requirement by filling company data (profile-aware:
 * profile 1 = root, profiles 2/3 = company.perfiles by numeroDePerfil), then
 * creates an appointment and optionally updates company (skipped for no-socios, tipoDeUsuario `"4"`). Revalidates the requirement
 * on open so company data and missing field maps come from the same fresh snapshot.
 * Fetches location reference data (countries, states, towns) once on open to avoid
 * refetch on profile change.
 * @module modals/AppointmentRegistration
 */

import useAppointmentRegistrationForm from "../../hooks/useAppointmentRegistrationForm.js";
import AppointmentRegistrationView from "./AppointmentRegistrationView.jsx";

/**
 * Modal for registering to a business event requirement with company data.
 * Supports multiple company profiles (root + perfiles); updates company (except no-socios)
 * and creates appointment on submit. May show a confirmation when event is at capacity.
 *
 * @param {Object} props
 * @param {React.RefObject<HTMLDialogElement>} props.dialogRef - Ref for the dialog element
 * @param {Object} [props.selectedRequirement] - Requirement to register for (includes datosEmpresaUsuario, requisitos, missingRequiredFields, etc.)
 * @param {Object} props.participant - Participating company (idEvento, idEmpresaCompradora)
 * @param {Object} props.eventData - Event (e.g. _id, cupoMaximo, fechaLimiteRegistro)
 * @param {Object} props.currentUser - Current user (e.g. tipoDeUsuario)
 * @param {boolean} props.open - Whether the modal is open
 * @param {function(): void} props.onClose - Called when the modal is closed
 * @param {function(): void} props.onSubmit - Called after successful save
 * @returns {JSX.Element}
 */
const AppointmentRegistration = (props) => {
  const viewModel = useAppointmentRegistrationForm(props);
  return <AppointmentRegistrationView {...viewModel} />;
};

export default AppointmentRegistration;
